import { useState, useContext, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { updateDoc } from "firebase/firestore"
import {
	Button,
	Center,
	Heading,
	FormControl,
	FormErrorMessage,
	FormLabel,
	Input,
	Image,
	Stack,
	Spinner,
	Text,
} from "@chakra-ui/react"
import Autocomplete from "react-google-autocomplete"
import PhoneInput from "react-phone-number-input/input"
import { isValidPhoneNumber } from "react-phone-number-input"

import { logUserEvent } from "../../firebase"
import { analytic_events } from "../../analytics"
import { UserContext } from "../../Contexts/UserContext"
import { Partners } from "../../partnershipData"

export const ClientProfile = () => {
	const navigate = useNavigate()
	const { userPlus } = useContext(UserContext)
	const [clientData, setClientData] = useState({
		name: null,
		email: null,
		phoneNumber: null,
		address: null,
	})
	const [pageLoading, setPageLoading] = useState(false)
	const [formError, setFormError] = useState(false)
	const apiKey = process.env.REACT_APP_GOOGLE_API_KEY

	const partner = Partners.find((p) => p.id === userPlus?.partner)

	useEffect(() => {
		if (userPlus === "pending") {
			return
		} else {
			setClientData({
				name: userPlus.name,
				email: userPlus.email,
				phoneNumber: userPlus.phoneNumber ? userPlus.phoneNumber : null,
				address: userPlus.address ? userPlus.address : null,
			})
		}
	}, [userPlus])

	const verifyClientInformation = () => {
		const hasError =
			!clientData.name ||
			(!clientData.email && !clientData.phoneNumber) ||
			(!clientData.phoneNumber && !isValidPhoneNumber(clientData.phoneNumber))

		if (hasError) {
			alert("Please correct fields before proceeding.")
		}
		setFormError(hasError)
		return hasError
	}

	const handleGenericSelectChange = (selectedOptions, fieldName) => {
		setClientData((prevData) => ({
			...prevData,
			[fieldName]: selectedOptions.value,
		}))
	}

	const onSubmit = async (e) => {
		e.preventDefault()
		setPageLoading(true)
		const hasError = verifyClientInformation()
		if (hasError) {
			setPageLoading(false)
			return
		}

		await updateDoc(userPlus.userDocRef, {
			_updatedAt: new Date(),
			name: clientData.name,
			phoneNumber: clientData.phoneNumber,
			address: clientData.address,
		})

		await logUserEvent(
			analytic_events.CLIENT_USER_PROFILE_UPDATE,
			userPlus.id,
			{
				name: clientData.name,
			}
		)
		navigate("/vendors")
	}

	const handleChange = (input, e) => {
		setClientData({ ...clientData, [input]: e })
	}

	if (pageLoading) {
		return (
			<Center mt="50px">
				<Stack>
					<Spinner m="auto" />
				</Stack>
			</Center>
		)
	}

	return (
		<Center
			flexDirection="column"
			maxW="629px"
			margin={{ base: "15px", md: "auto" }}
		>
			<Stack w="100%" gap={3}>
				<Heading size="lg" fontWeight="bold" mt="30px">
					Profile
				</Heading>
				<Text>Set up your account information.</Text>
				<Image
					src={
						partner
							? `assets/${partner.id}_house_landscape.svg`
							: "/assets/house_landscape.png"
					}
					alt="vendor house"
					m="auto"
				/>
				<FormControl isInvalid={formError && clientData.name === ""}>
					<FormLabel>Name</FormLabel>
					<Input
						bgColor="white"
						type="text"
						name="name"
						placeholder="Enter your name"
						value={clientData.name}
						onChange={(e) => {
							handleChange("name", e.target.value)
						}}
					/>
					{formError && clientData.name === "" && (
						<FormErrorMessage>Name is required.</FormErrorMessage>
					)}
				</FormControl>
				<FormControl isDisabled={userPlus.email}>
					<FormLabel>Email</FormLabel>
					<Input
						bgColor="white"
						type="text"
						name="Email"
						placeholder="Enter your email"
						value={clientData.email}
						onChange={(e) => {
							handleChange("email", e.target.value.toLowerCase())
						}}
					/>
				</FormControl>
				<FormControl
					isInvalid={
						formError &&
						(clientData.phoneNumber === "" ||
							(clientData.phoneNumber &&
								!isValidPhoneNumber(clientData.phoneNumber)))
					}
				>
					<FormLabel>Phone Number</FormLabel>
					<PhoneInput
						country="US"
						style={{
							width: "100%",
							height: "40px",
							padding: "0.5rem 0.75rem",
							border:
								formError &&
								(clientData.phoneNumber === "" ||
									(clientData.phoneNumber &&
										!isValidPhoneNumber(clientData.phoneNumber)))
									? "1px solid #F05252"
									: "1px solid #CBD5E0",
							borderRadius: "0.375rem",
							outlineColor:
								formError &&
								(clientData.phoneNumber === "" ||
									(clientData.phoneNumber &&
										!isValidPhoneNumber(clientData.phoneNumber)))
									? "#F05252"
									: "#CBD5E0",
						}}
						placeholder="Enter phone number"
						value={clientData.phoneNumber}
						onChange={(e) => {
							setClientData((prevclientData) => ({
								...prevclientData,
								phoneNumber: e,
							}))
						}}
						onFocus={(e) => {
							e.target.style.borderColor = "#4299e1"
							e.target.style.boxShadow = "0 0 0 3px rgba(66, 153, 225, 0.1)"
							e.target.style.outlineColor = "#4299e1"
						}}
						onBlur={(e) => {
							e.target.style.borderColor =
								formError &&
								(clientData.phoneNumber === "" ||
									(clientData.phoneNumber &&
										!isValidPhoneNumber(clientData.phoneNumber)))
									? "#F05252"
									: "#CBD5E0"
							e.target.style.boxShadow =
								formError &&
								(clientData.phoneNumber === "" ||
									(clientData.phoneNumber &&
										!isValidPhoneNumber(clientData.phoneNumber)))
									? "0 0 0 1px #F05252"
									: "none"
						}}
					/>
					{formError &&
						(clientData.phoneNumber === "" ||
							(clientData.phoneNumber &&
								!isValidPhoneNumber(clientData.phoneNumber))) && (
							<FormErrorMessage>
								Please enter a valid phone number.
							</FormErrorMessage>
						)}
				</FormControl>
				<FormControl>
					<FormLabel>Address</FormLabel>
					<Autocomplete
						isInvalid={formError && clientData.address === ""}
						style={{
							width: "100%",
							height: "40px",
							borderRadius: "5px",
							outline: "2px solid transparent",
							border:
								formError && clientData.address === ""
									? "1px solid red"
									: "1px solid #e2e2ea",
							paddingLeft: "15px",
						}}
						apiKey={apiKey}
						value={clientData.address}
						onPlaceSelected={(place) => {
							handleGenericSelectChange(
								{ value: place.formatted_address },
								"address"
							)
						}}
						options={{ types: [] }}
					/>
				</FormControl>
				<Button
					w="100%"
					bgColor="borderColor"
					color="white"
					onClick={onSubmit}
					mt="30px"
					mb="40px"
				>
					Save
				</Button>
			</Stack>
		</Center>
	)
}
